import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import { uniqueId } from 'lodash-es';
import { type Component, ref, type Ref } from 'vue';
import type { I18TParams } from '@/i18n.ts';
import Validator from '@/modules/common/utils/validator.ts';

export type ValidatorFn<T> = (value: T) => string | void | I18TParams;

export type SchemaControlIndent = 'default' | 'spacer' | 'spread';

export type SchemaControlGender = 'm' | 'n' | 'f' | 'pl';

type SchemaControlSlot = {
  from: string;
  to: string;
};

export type SchemaControlSlotName = string | SchemaControlSlot;

export type SchemaControlArgs = {
  name: string;
  label?: string;
  slot?: SchemaControlSlotName;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  gender?: SchemaControlGender;
  collapsible?: boolean;
  showCount?: boolean;
  indent?: SchemaControlIndent;
};

export abstract class SchemaControl<T> {
  abstract readonly component: Component;
  readonly id = uniqueId(IdPrefix.SchemaControl);
  readonly name: string;
  readonly label?: string;
  readonly slot?: SchemaControlSlot;
  readonly required: boolean;
  readonly disabled: boolean;
  readonly readonly?: boolean;
  readonly gender?: SchemaControlGender;
  readonly collapsible: boolean;
  readonly showCount?: boolean;
  readonly indent: SchemaControlIndent;
  readonly focusOnLabelClick: boolean = false;
  readonly validators: ValidatorFn<T>[] = [];
  aiGenerating: Ref<boolean>;

  protected constructor({
    name,
    label,
    slot,
    required,
    disabled,
    readonly,
    gender,
    collapsible,
    showCount,
    indent,
  }: SchemaControlArgs) {
    this.name = name;
    this.label = label;
    this.slot = this.getSlot(slot);
    this.required = !!required;
    this.disabled = !!disabled;
    this.readonly = readonly;
    this.gender = gender;
    this.collapsible = !!collapsible;
    this.showCount = !!showCount;
    this.indent = indent || 'default';
    this.setBaseValidators(this.required);
    this.aiGenerating = ref(false);
  }

  private getSlot(slotName?: SchemaControlSlotName) {
    if (!slotName) {
      return undefined;
    }

    return {
      from: typeof slotName === 'string' ? slotName : slotName.from,
      to: typeof slotName === 'string' ? 'default' : slotName.to,
    };
  }

  private setBaseValidators(required: boolean) {
    if (required) {
      this.addValidator(Validator.required);
    }
  }

  protected addValidator(validator: ValidatorFn<T>) {
    this.validators.push(validator);
  }
}
